import { useTranslation } from "react-i18next";
import { useDashboard } from "../../hooks/queries";
import { money } from "../../lib/format";
import { Pill } from "../ui/Badges";

const CAT_ORDER = ["pass", "party_package", "school_package", "fnb", "merch", "addon", "party_addon", "other"];

export function KpiCards() {
  const { t } = useTranslation();
  const { data } = useDashboard();
  if (!data) return <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{[0, 1, 2, 3].map((i) => <div key={i} className="card h-36 animate-pulse bg-gray-100" />)}</div>;

  const cats = [...data.revenue.byCategory].sort((a, b) => CAT_ORDER.indexOf(a.kind) - CAT_ORDER.indexOf(b.kind)).slice(0, 3);
  const vs = data.revenue.vsYesterdayPercent;
  const cur = t("currency");

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <div className="card p-4 sm:p-5">
        <div className="mb-2 flex items-start justify-between">
          <span className="text-xs font-black uppercase tracking-wider text-gray-500">{t("kpi.revenue")}</span>
          {vs !== null && <Pill tone={vs >= 0 ? "green" : "red"}>{vs >= 0 ? "+" : ""}<span className="num">{vs}%</span> {t("kpi.vsYesterday")}</Pill>}
        </div>
        <div className="num font-mono text-2xl font-black tracking-tight">{money(data.revenue.gross)} <span className="text-sm font-bold text-cosmic-purple">{cur}</span></div>
        <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 border-t border-gray-100 pt-2.5 text-[11px] font-medium text-gray-500">
          {cats.map((c) => <span key={c.kind}>{t(`kind.${c.kind}`, { defaultValue: c.kind })}: <strong className="num text-cosmic-navy">{money(c.total)}</strong></span>)}
        </div>
      </div>

      <div className="card border-amber-200 bg-amber-50/40 p-4 sm:p-5">
        <div className="mb-2 flex items-start justify-between">
          <span className="text-xs font-black uppercase tracking-wider text-amber-900">{t("kpi.debts")}</span>
          {data.receivables.overdueCount > 0 && <span className="animate-pulse rounded-full bg-solar-orange px-2 py-0.5 text-[10px] font-black text-white">{t("kpi.followUp")}</span>}
        </div>
        <div className="num font-mono text-2xl font-black tracking-tight text-solar-orange">{money(data.receivables.pending)} <span className="text-sm font-bold text-amber-900">{cur}</span></div>
        <div className="mt-3 border-t border-amber-200/60 pt-2.5 text-[11px] font-semibold text-amber-800">{t("kpi.overdueAccounts", { count: data.receivables.overdueCount })}</div>
      </div>

      <div className="card p-4 sm:p-5">
        <div className="mb-2 flex items-start justify-between">
          <span className="text-xs font-black uppercase tracking-wider text-gray-500">{t("kpi.capacity")}</span>
          <Pill tone={data.capacity.percent >= 90 ? "red" : "green"}><span className="num">{data.capacity.percent}%</span></Pill>
        </div>
        <div className="num font-mono text-2xl font-black tracking-tight">{data.capacity.occupied} / {data.capacity.capacity} <span className="text-sm font-bold text-gray-500">{t("capacity.kids")}</span></div>
        <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2.5 text-[11px] font-medium text-gray-500">
          <span className="font-bold text-emerald-600">{t("capacity.available", { count: data.capacity.available })}</span>
          <span>{t("kpi.entriesToday", { count: data.entriesToday })}</span>
        </div>
      </div>

      <div className="card border-purple-200 bg-purple-50/50 p-4 sm:p-5">
        <div className="mb-2 flex items-start justify-between">
          <span className="text-xs font-black uppercase tracking-wider text-gray-700">{t("kpi.stock")}</span>
          {data.lowStockItems > 0 && <Pill tone="red">{t("kpi.alerts", { count: data.lowStockItems })}</Pill>}
        </div>
        <div className="num font-mono text-2xl font-black tracking-tight text-cosmic-purple">{data.lowStockItems} <span className="text-sm font-bold text-gray-700">{t("kpi.belowPar")}</span></div>
        <div className="mt-3 border-t border-purple-200 pt-2.5 text-[11px] font-bold text-purple-700">{t("kpi.partiesToday", { count: data.partiesToday })}</div>
      </div>
    </div>
  );
}
